/**
 * Row component module.
 * @module shared/components/Row
 *
 */
import React, {Children} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {fill, fillHeight, flexMap, flexWrap, layoutAttribute} from './cssUtils';
import {getBlackWhiteWithOpacity} from '../utils/colors';

const _Row = styled.div.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => !['wrap'].includes(prop) && defaultValidatorFn(prop),
})`
  display: flex;
  -webkit-flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  ${flexWrap};
  ${(props) => props.verticalAlign ? 'display: inline-flex;flex-direction: row;align-items: center;margin-top: 4px;' : ''};
  -webkit-justify-content: ${props => flexMap[props.justifySubRow || 'center']};
  justify-content: ${props => flexMap[props.justifySubRow || 'center']};
  -webkit-align-content: ${props => flexMap[props.alignSubRow || 'top']};
  align-content: ${props => flexMap[props.alignSubRow || 'top']};
  -webkit-align-items: ${props => flexMap[props.alignItems || 'center']};
  align-items: ${props => flexMap[props.alignItems || 'center']};
  ${props => fill(props.width)};
  ${props => props.height && fillHeight(props.height === 'fill' ? null : props.height)};
  ${props => props.maxHeight && `max-height: ${props.maxHeight}`};
  ${props => props.minHeight && `min-height: ${props.minHeight}`};
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${props => props.minWidth && `min-width: ${props.minWidth}`};
  ${props => props.margin && `margin: ${props.margin}`};
  ${props => props.backgroundColor && `background-color: ${props.backgroundColor}`};
  ${props => props.border && `border: ${props.border}`};
  ${props => props.borderBottom && `border-bottom: ${props.borderBottom}`};
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`};
  ${layoutAttribute.css};
  ${props => props.showDividers && `
  overflow-x: hidden;
  &>*{
    margin-left: 6px;
  }
  &>*:first-child {
    margin-left: 0px;
  }`};
  ${props => props.action && `
  cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
  :hover {
    border-radius: 4px;
    background-color: ${getBlackWhiteWithOpacity(0.05)};
  };
  transition: background-color 0.5s;
  `};
`;

const _Divider = styled.div`
  width: 1px;
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
  align-self: stretch;
  flex-grow: 0;
  flex-shrink: 0;
  margin-left: 5px;
  margin-right: -1px;
`;

const Row = React.forwardRef((props, ref) => {
  const lastChildIndex = Children.count(Array.isArray(props.children) && props.children.filter(x=>!!x) || props.children) - 1;
  return <_Row{...props} onClick={!props.disabled ? props.action : undefined} ref={ref}>{
    props.showDividers
      ? Children.map(props.children, (child, index) => [child, index < lastChildIndex && <_Divider/>].filter(c => !!c))
      : props.children
  }</_Row>;
});

Row.displayName = 'Row';

export default Row;

Row.propTypes = {
  /**
   * Optional function to execute on click
   */
  action: PropTypes.func,
  /**
   * Whether the action is disabled
   */
  disabled: PropTypes.bool,
  /**
   * height of the row
   */
  height: PropTypes.string,
  /**
   * width of the row, defaults to Fill
   */
  width: PropTypes.string,
  /**
   * max-height of the row
   */
  maxHeight: PropTypes.string,
  /**
   * min-height of the row
   */
  minHeight: PropTypes.string,
  /**
   * max-width of the row
   */
  maxWidth: PropTypes.string,
  /**
   * min-width of the row
   */
  minWidth: PropTypes.string,
  /**
   * margin of the row
   */
  margin: PropTypes.string,
  /**
   * background-color of the row
   */
  backgroundColor: PropTypes.string,
  /**
   * border of the row
   */
  border: PropTypes.string,
  /**
   * bottom border of the row
   */
  borderBottom: PropTypes.string,
  /**
   * border-radius of the row
   */
  borderRadius: PropTypes.string,
  /**
   * Does the row flex-wrap: wrap (True) or nowrap (False).
   */
  wrap: PropTypes.bool,
  /**
   * justify-content, defaults to center, see cssUtils.flexWrap for mapping // ETODO Link to flexWrap
   */
  justifySubRow: PropTypes.oneOf(['left', 'right', 'space between', 'space around', 'center']),
  /**
   * align-content, defaults to flex-start, see cssUtils.flexWrap for mapping // EETODO Link to flexWrap
   */
  alignSubRow: PropTypes.oneOf(['stretch', 'top', 'bottom', 'space between', 'space around', 'middle']),
  /**
   * align-items, defaults to center, see cssUtils.flexWrap for mapping // ETODO Link to flexWrap
   */
  alignItems: PropTypes.oneOf(['stretch', 'top', 'bottom', 'space between', 'space around', 'middle']),
  /**
   * If true, displays a divider line between each component of the row
   */
  showDividers: PropTypes.bool,
  /**
   * Content of the row
   */
  children: PropTypes.node.isRequired,
  layout: layoutAttribute.propTypes,
  /**
   * If true reverse the row direction to be rtl.
   */
  reverse: PropTypes.bool,
  verticalAlign: PropTypes.bool,
};
